/**
 * `POST /venue/v1/manager/venues` 요청/응답 타입 사본 — 허브 `backend-facecock-was/task-747/api-spec.md`
 * §1 그대로. `venueProfileTypes.ts`·`courtTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한
 * 여기서 필드를 파생하거나 이름을 바꾸지 않는다.
 *
 * 🚨 이 웹 화면은 이미 최소 1개 구장을 관리 중인 로그인 상태에서만 쓰인다(design.md §2 "진입 경로").
 */

/** api-spec §1 — forward-only. */
export type VenueFacilityType = 'BADMINTON' | 'TENNIS' | 'FUTSAL' | 'SOCCER' | 'BASKETBALL' | 'ETC'

/**
 * 시설 종류 선택 UI 값 목록. Figma·기존 코드 어디에도 대응 선택 UI 가 없어 이 화면을 위해 신설한다
 * (설계 §2 자율 결정). 기본 선택값은 서비스 주 종목인 배드민턴.
 */
export const VENUE_FACILITY_TYPE_CATALOG: { value: VenueFacilityType; label: string }[] = [
  { value: 'BADMINTON', label: '배드민턴' },
  { value: 'TENNIS', label: '테니스' },
  { value: 'FUTSAL', label: '풋살' },
  { value: 'SOCCER', label: '축구' },
  { value: 'BASKETBALL', label: '농구' },
  { value: 'ETC', label: '기타' },
]

/**
 * `courts[]` 요청 원소 — 이 화면이 실제로 채우는 필드만(이슈 명시 4항목: 이름·운영시간 시작/종료·
 * 예약 단위·최대 예약 시간). `weekdayHourlyFee`·`weekendHourlyFee`·`operatingDays` 는 이 화면이
 * 보내지 않는다 — 서버가 안전한 기본값(0원 / 7일 전체 운영)을 채운다(설계 §2 자율 결정).
 */
export interface VenueRegisterCourtInput {
  name: string
  /** "HH:mm". */
  operatingStartTime: string
  /** "HH:mm". "24:00"(자정 마감) 허용. */
  operatingEndTime: string
  reservationUnitMinutes: number
  maxReservationMinutes: number
}

/** `POST` 요청 바디(api-spec §1). */
export interface VenueRegisterSaveInput {
  name: string
  facilityType: VenueFacilityType
  address: string
  detailAddress: string | null
  contact: string | null
  locationGuide: string | null
  usageGuide: string | null
  /** 🚨 이 화면은 입력 UI 가 없어 항상 `null` 로 고정 전송한다(설계 §2 자율 결정). */
  additionalNotice: string | null
  amenities: string[]
  rentalEquipments: string[]
  /** 🚨 `#865` 로 3상태(`null`=미입력)가 폐기되고 non-null 로 확정됐다 — 미전송 시 서버가 `false` 로 저장한다. */
  parkingAvailable: boolean
  showerAvailable: boolean
  lockerRoomAvailable: boolean
  /** 🚨 선택이다 — 생략하면 구장만 생성된다(api-spec §1, 코트는 나중에 채울 수 있다). */
  courts?: VenueRegisterCourtInput[]
}

/** `201` 응답(api-spec §1). */
export interface VenueRegisterResult {
  baseTime: string
  venueId: string
  name: string
  /** 항상 `UNVERIFIED`(기본 미완료). */
  operationVerificationStatus: string
  operationVerificationStatusLabel: string
  /** 항상 `false`(기본 OFF). */
  reservationEnabled: boolean
  /** 항상 `false`(목록·검색 비노출). */
  userVisible: boolean
  /** 요청 순서 그대로. 코트를 안 보냈으면 빈 배열. */
  courtIds: number[]
  /** forward-only. 알려진 값: `OPERATION_VERIFICATION`·`COURT_REGISTRATION`(이미 끝난 단계는 빠진다). */
  nextSteps: string[]
}
